// =============================================================================
// 📁 File: src/pages/ddo/DDOSubmissionReviewPage.tsx
// =============================================================================
// CTO Recovery Prompt — Production Hardened Version
//
// Guarantees:
// - canonical workflow contract (action_code)
// - no race transitions
// - no stale workflow execution
// - backend remains authority
// - sequential state reload
// - tenant-safe routing
// =============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "@/utils/axiosInstance";
import {
  DDOAvailableAction,
  fetchDDOAvailableActions,
} from "@/api/ddoApi";
import {
  SubmissionTimelineResponse,
  fetchSubmissionTimeline,
  formatWorkflowActionLabel,
} from "@/api/workflowApi";
import { formatWorkflowStatusLabel } from "@/utils/semanticLabels";

import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  FileWarning,
  FileText,
  History,
  ShieldCheck,
} from "lucide-react";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface SubmissionDetail {
  id: number;
  student_id: number;
  status: string | null;
  program_code: string | null;
  academic_year: string | null;
  assigned_at: string | null;
  locked: boolean;
  deleted: boolean;
  data: Record<string, unknown>;
}

interface TransitionRequest {
  action_code: string;
  remarks?: string;
}

type TimelineTone = "default" | "attention" | "critical" | "success";
type SignalType = "informational" | "attention" | "risk" | "anomaly" | "blocked";

interface AttentionSignal {
  key: string;
  type: SignalType;
  title: string;
  detail: string;
}

interface DiligenceDocument {
  document_id: string;
  document_type: string;
  original_filename?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
  uploaded_at?: string | null;
  status?: string | null;
  scan_status?: string | null;
  scan_mode?: string | null;
  scanned_at?: string | null;
  storage_provider?: string | null;
}

interface DiligenceComparison {
  field_name: string;
  applicant_claim_value: unknown;
  ocr_extracted_value: unknown;
  ddo_verified_value: unknown;
  discrepancy_detected: boolean;
}

interface DiligenceProjection {
  submission_id: number;
  documents: DiligenceDocument[];
  documents_opened: Array<{
    document_id: string;
    document_type?: string | null;
    opened_at: string;
    actor_id: number;
  }>;
  ocr_candidates: Record<
    string,
    {
      value: unknown;
      confidence?: number | null;
      source_document_id?: string | null;
      authoritative: boolean;
    }
  >;
  ocr_warning: string;
  ddo_values: Record<string, unknown>;
  diligence_submitted: boolean;
  applicant_claims_visible: boolean;
  applicant_claims: Record<string, unknown>;
  comparisons: DiligenceComparison[];
  resolutions: Record<
    string,
    {
      final_resolved_value: unknown;
      governance_resolved_value: unknown;
      resolution_outcome: string;
      reason: string;
      resolved_by: number;
      resolved_by_role?: string | null;
      resolved_at: string;
    }
  >;
  unresolved_discrepancy_fields: string[];
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const getActionVariant = (
  code: string
): "default" | "secondary" | "destructive" | "outline" => {
  const c = code.toUpperCase();

  if (c.includes("REJECT")) return "destructive";
  if (c.includes("RESUBMISSION")) return "secondary";
  if (c.includes("INTERVIEW")) return "secondary";

  return "outline";
};

const formatStatusLabel = (value: string | null | undefined): string => {
  if (!value) return "Unknown";
  return formatWorkflowStatusLabel(value);
};

const formatTimestamp = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const daysSince = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const diff = Date.now() - parsed.getTime();
  return Math.max(0, Math.floor(diff / 86400000));
};

const formatAge = (days: number | null): string => {
  if (days == null) return "age unavailable";
  if (days === 0) return "today";
  if (days === 1) return "1 day";
  return `${days} days`;
};

const toneClasses: Record<TimelineTone, string> = {
  default: "border-gray-200 bg-white",
  attention: "border-amber-200 bg-amber-50",
  critical: "border-red-200 bg-red-50",
  success: "border-emerald-200 bg-emerald-50",
};

const signalClasses: Record<SignalType, string> = {
  informational: "border-gray-200 bg-white",
  attention: "border-blue-200 bg-blue-50",
  risk: "border-amber-200 bg-amber-50",
  anomaly: "border-red-200 bg-red-50",
  blocked: "border-red-200 bg-red-50",
};

const signalLabels: Record<SignalType, string> = {
  informational: "Informational",
  attention: "Attention",
  risk: "Risk",
  anomaly: "Anomaly",
  blocked: "Blocked",
};

const signalPriority: Record<SignalType, number> = {
  blocked: 0,
  anomaly: 1,
  risk: 2,
  attention: 3,
  informational: 4,
};

const dotClasses: Record<TimelineTone, string> = {
  default: "bg-gray-400",
  attention: "bg-amber-500",
  critical: "bg-red-500",
  success: "bg-emerald-500",
};

const getEventTone = (actionCode: string, toStatus: string): TimelineTone => {
  const action = actionCode.toUpperCase();
  const status = toStatus.toUpperCase();

  if (action.includes("REJECT") || status.includes("REJECT") || status === "CLOSED") {
    return "critical";
  }
  if (
    action.includes("RESUBMISSION") ||
    action.includes("ESCALATE") ||
    action.includes("MANAGEMENT") ||
    action.includes("VETO") ||
    status.includes("MGMT")
  ) {
    return "attention";
  }
  if (action.includes("APPROVE") || action.includes("VERIFY") || action.includes("DISBURSE")) {
    return "success";
  }
  return "default";
};

const getEventIcon = (tone: TimelineTone) => {
  if (tone === "critical") return AlertTriangle;
  if (tone === "attention") return FileWarning;
  if (tone === "success") return CheckCircle2;
  return History;
};

const getSignalIcon = (type: SignalType) => {
  if (type === "blocked") return AlertCircle;
  if (type === "anomaly") return AlertTriangle;
  if (type === "risk") return Clock3;
  if (type === "attention") return FileWarning;
  return ShieldCheck;
};

const buildAttentionSignals = (
  submission: SubmissionDetail,
  timeline: SubmissionTimelineResponse | null,
  actions: DDOAvailableAction[]
): AttentionSignal[] => {
  const signals: AttentionSignal[] = [];
  const assignedAge = daysSince(submission.assigned_at);

  if (assignedAge != null && assignedAge >= 7) {
    signals.push({
      key: "stale-assignment-critical",
      type: "risk",
      title: "Assignment aging",
      detail: `Assigned to this queue for ${formatAge(assignedAge)}.`,
    });
  } else if (assignedAge != null && assignedAge >= 3) {
    signals.push({
      key: "stale-assignment-attention",
      type: "attention",
      title: "Assignment aging",
      detail: `Assigned to this queue for ${formatAge(assignedAge)}.`,
    });
  }

  if (submission.locked) {
    signals.push({
      key: "locked",
      type: "blocked",
      title: "Locked submission",
      detail: "Submission is currently locked for normal DDO handling.",
    });
  }

  if (actions.length === 0) {
    signals.push({
      key: "no-actions",
      type: "informational",
      title: "No available actions",
      detail: "Backend did not expose an executable workflow action for this user.",
    });
  }

  const events = timeline?.workflow_events ?? [];
  const auditMarkers = timeline?.audit_markers ?? [];
  const escalated = events.some((event) => {
    const action = event.action_code.toUpperCase();
    const status = event.to_status.toUpperCase();
    return action.includes("MANAGEMENT") || action.includes("ESCALATE") || status.includes("MGMT");
  });
  const resubmission = events.some((event) =>
    event.action_code.toUpperCase().includes("RESUBMISSION")
  );
  const anomalyMarkers = auditMarkers.filter((marker) => {
    const action = marker.action.toUpperCase();
    return (
      action.includes("FAIL") ||
      action.includes("ERROR") ||
      action.includes("REPLAY") ||
      action.includes("CONFLICT") ||
      action.includes("DUPLICATE")
    );
  });

  if (escalated) {
    signals.push({
      key: "escalated",
      type: "attention",
      title: "Escalation path",
      detail: "Timeline includes a management or escalation transition.",
    });
  }

  if (resubmission) {
    signals.push({
      key: "resubmission",
      type: "attention",
      title: "Returned for correction",
      detail: "Timeline includes a resubmission request.",
    });
  }

  if (anomalyMarkers.length > 0) {
    signals.push({
      key: "audit-anomaly",
      type: "anomaly",
      title: "Forensic anomaly marker",
      detail: `${anomalyMarkers.length} audit marker(s) indicate failure, replay, conflict, or duplication.`,
    });
  }

  return signals.sort((a, b) => signalPriority[a.type] - signalPriority[b.type]);
};

const buildReviewBlocker = (
  submission: SubmissionDetail,
  diligence: DiligenceProjection | null,
  actions: DDOAvailableAction[]
): { label: string; detail: string; tone: SignalType } => {
  if (submission.locked) {
    return {
      label: "Workflow progression blocked",
      detail: "This submission is locked. DDO actions should not be attempted until it is unlocked.",
      tone: "blocked",
    };
  }

  if (!diligence) {
    return {
      label: "Diligence state unavailable",
      detail: "The backend did not return a diligence projection, so DDO review cannot be trusted yet.",
      tone: "anomaly",
    };
  }

  if (
    !diligence.diligence_submitted &&
    diligence.documents.length > 0 &&
    diligence.documents_opened.length === 0
  ) {
    return {
      label: "Workflow progression blocked",
      detail: "Review at least one supporting document before blind diligence is submitted.",
      tone: "blocked",
    };
  }

  if (!diligence.diligence_submitted) {
    return {
      label: "Blind diligence required",
      detail: "Applicant claims remain hidden until independent DDO values are submitted.",
      tone: "risk",
    };
  }

  if (diligence.unresolved_discrepancy_fields.length > 0) {
    return {
      label: "Workflow progression blocked",
      detail: `${diligence.unresolved_discrepancy_fields.length} discrepancy field(s) must be resolved before escalation.`,
      tone: "blocked",
    };
  }

  if (actions.length === 0) {
    return {
      label: "No DDO workflow action available",
      detail: "Backend did not expose an executable DDO action for the current workflow state.",
      tone: "informational",
    };
  }

  return {
    label: "Ready for workflow action",
    detail: `${actions.length} backend-authorized DDO action(s) are currently available.`,
    tone: "attention",
  };
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const DDOSubmissionReviewPage: React.FC = () => {
  const { tenantSlug, submissionId } = useParams<{
    tenantSlug: string;
    submissionId: string;
  }>();

  const navigate = useNavigate();

  const id = useMemo(() => {
    if (!submissionId) return null;
    const parsed = Number(submissionId);
    return Number.isNaN(parsed) ? null : parsed;
  }, [submissionId]);

  const [submission, setSubmission] = useState<SubmissionDetail | null>(null);
  const [actions, setActions] = useState<DDOAvailableAction[]>([]);
  const [timeline, setTimeline] = useState<SubmissionTimelineResponse | null>(
    null
  );
  const [diligence, setDiligence] = useState<DiligenceProjection | null>(null);
  const [diligenceRows, setDiligenceRows] = useState([
    { field_name: "", value: "" },
  ]);
  const [resolutionDrafts, setResolutionDrafts] = useState<
    Record<string, { finalValue: string; reason: string }>
  >({});

  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [diligenceBusy, setDiligenceBusy] = useState(false);
  const [transitioningAction, setTransitioningAction] = useState<string | null>(
    null
  );

  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState("");

  // HARD LOCK to prevent duplicate transitions
  const transitionLock = useRef(false);

  // ---------------------------------------------------------------------------
  // Load Page Data
  // ---------------------------------------------------------------------------

  const loadPageData = useCallback(
    async (mode: "initial" | "refresh" = "initial") => {
      if (!tenantSlug || !id) {
        setError("Invalid route parameters.");
        setLoading(false);
        return;
      }

      try {
        if (mode === "initial") setLoading(true);
        else setReloading(true);

        setError("");

        // IMPORTANT — sequential to avoid workflow mismatch
        const submissionRes = await axiosInstance.get<SubmissionDetail>(
          `/ddo/submissions/${id}`
        );

        setSubmission(submissionRes.data);

        try {
          const actionsRes = await fetchDDOAvailableActions(id);
          setActions(actionsRes.actions ?? []);
        } catch (actionsErr) {
          console.error("Load submission actions failed:", actionsErr);
          setActions([]);
        }

        try {
          const timelineRes = await fetchSubmissionTimeline(id);
          setTimeline(timelineRes);
        } catch (timelineErr) {
          console.error("Load submission timeline failed:", timelineErr);
          setTimeline(null);
        }

        try {
          const diligenceRes = await axiosInstance.get<DiligenceProjection>(
            `/ddo/submissions/${id}/diligence`
          );
          setDiligence(diligenceRes.data);
        } catch (diligenceErr) {
          console.error("Load diligence projection failed:", diligenceErr);
          setDiligence(null);
        }
      } catch (err: any) {
        console.error("Load submission review failed:", err);

        if (err?.response?.status === 404) {
          setError("Submission not found or not assigned to you.");
        } else if (err?.response?.status === 403) {
          setError("You are not authorized to view this submission.");
        } else if (err?.response?.data?.detail) {
          setError(String(err.response.data.detail));
        } else {
          setError("Failed to load submission review page.");
        }

        setSubmission(null);
        setActions([]);
        setTimeline(null);
        setDiligence(null);
      } finally {
        setLoading(false);
        setReloading(false);
      }
    },
    [tenantSlug, id]
  );

  const refreshDiligence = useCallback(async () => {
    if (!id) return;
    const diligenceRes = await axiosInstance.get<DiligenceProjection>(
      `/ddo/submissions/${id}/diligence`
    );
    setDiligence(diligenceRes.data);
  }, [id]);

  const handleDocumentOpen = useCallback(
    async (documentId: string) => {
      if (!submission) return;
      try {
        setDiligenceBusy(true);
        setError("");
        const response = await axiosInstance.post<DiligenceProjection>(
          `/ddo/submissions/${submission.id}/documents/${documentId}/open`
        );
        setDiligence(response.data);
      } catch (err: any) {
        setError(err?.response?.data?.detail || "Failed to record document review.");
      } finally {
        setDiligenceBusy(false);
      }
    },
    [submission]
  );

  const submitDiligence = useCallback(async () => {
    if (!submission) return;
    const verifiedValues: Record<string, string> = {};
    for (const row of diligenceRows) {
      const key = row.field_name.trim();
      if (key) verifiedValues[key] = row.value.trim();
    }
    if (Object.keys(verifiedValues).length === 0) {
      setError("Enter at least one independently verified diligence field.");
      return;
    }
    try {
      setDiligenceBusy(true);
      setError("");
      const response = await axiosInstance.post<DiligenceProjection>(
        `/ddo/submissions/${submission.id}/diligence`,
        { verified_values: verifiedValues }
      );
      setDiligence(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to submit diligence verification.");
    } finally {
      setDiligenceBusy(false);
    }
  }, [submission, diligenceRows]);

  const resolveDiligenceField = useCallback(
    async (fieldName: string) => {
      if (!submission) return;
      const draft = resolutionDrafts[fieldName] || { finalValue: "", reason: "" };
      if (!draft.reason.trim()) {
        setError("Resolution reason is required.");
        return;
      }
      try {
        setDiligenceBusy(true);
        setError("");
        const response = await axiosInstance.post<DiligenceProjection>(
          `/ddo/submissions/${submission.id}/diligence/resolve`,
          {
            field_name: fieldName,
            governance_resolved_value: draft.finalValue,
            resolution_outcome: "resolved",
            reason: draft.reason,
          }
        );
        setDiligence(response.data);
      } catch (err: any) {
        setError(err?.response?.data?.detail || "Failed to resolve discrepancy.");
      } finally {
        setDiligenceBusy(false);
      }
    },
    [submission, resolutionDrafts]
  );

  useEffect(() => {
    void loadPageData("initial");
  }, [loadPageData]);

  // ---------------------------------------------------------------------------
  // Workflow Transition (Hardened)
  // ---------------------------------------------------------------------------

  const handleTransition = useCallback(
    async (actionCode: string) => {
      if (!submission) return;

      // Prevent race clicks
      if (transitionLock.current) return;

      // Verify action still valid
      const validAction = actions.find((a) => a.action_code === actionCode);
      if (!validAction) {
        setError("Workflow state changed. Please refresh.");
        return;
      }

      try {
        transitionLock.current = true;

        setTransitioningAction(actionCode);
        setError("");

        const payload: TransitionRequest = {
          action_code: actionCode,
          remarks: remarks.trim() || undefined,
        };

        await axiosInstance.post(
          `/ddo/submissions/${submission.id}/transition`,
          payload
        );

        // Backend remains authority
        await loadPageData("refresh");
        await refreshDiligence();

        setRemarks("");
      } catch (err: any) {
        console.error("Transition failed:", err);

        if (err?.response?.data?.detail) {
          setError(String(err.response.data.detail));
        } else {
          setError("Workflow transition failed.");
        }
      } finally {
        transitionLock.current = false;
        setTransitioningAction(null);
      }
    },
    [submission, actions, remarks, loadPageData, refreshDiligence]
  );

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error && !submission) {
    return (
      <div className="p-6 text-red-600 flex items-center gap-2">
        <AlertCircle className="h-4 w-4" />
        {error}
      </div>
    );
  }

  if (!submission) return null;

  const attentionSignals = buildAttentionSignals(submission, timeline, actions);
  const visibleAttentionSignals = attentionSignals.slice(0, 3);
  const hiddenAttentionCount = Math.max(
    0,
    attentionSignals.length - visibleAttentionSignals.length
  );
  const reviewBlocker = buildReviewBlocker(submission, diligence, actions);
  const openedDocumentCount = diligence?.documents_opened.length ?? 0;
  const totalDocumentCount = diligence?.documents.length ?? 0;
  const assignedAge = daysSince(submission.assigned_at);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="p-6 space-y-6">
      {/* Header */}

      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Submission #{submission.id}</h1>

        <Badge variant="secondary">{submission.status ?? "Unknown"}</Badge>
      </div>

      {/* Metadata */}

      <Card>
        <CardHeader>
          <CardTitle>Submission Details</CardTitle>
        </CardHeader>

        <CardContent className="text-sm space-y-2">
          <div>Student ID: {submission.student_id}</div>
          <div>Program: {submission.program_code ?? "-"}</div>
          <div>Academic Year: {submission.academic_year ?? "-"}</div>
          <div>Assigned At: {submission.assigned_at ?? "-"}</div>
          <div>Locked: {submission.locked ? "Yes" : "No"}</div>
        </CardContent>
      </Card>

      {/* Operational Truth */}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            DDO Operational State
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className={`rounded-md border p-3 text-sm ${signalClasses[reviewBlocker.tone]}`}>
            <div className="font-medium">{reviewBlocker.label}</div>
            <div className="mt-1 text-muted-foreground">{reviewBlocker.detail}</div>
          </div>

          <div className="grid gap-3 md:grid-cols-4">
            <div className="rounded-md border bg-white p-3 text-sm">
              <div className="text-xs font-medium uppercase text-muted-foreground">
                Assignment ownership
              </div>
              <div className="mt-1 font-medium">Assigned to current DDO</div>
              <div className="text-xs text-muted-foreground">
                {submission.assigned_at
                  ? `Assigned ${formatAge(assignedAge)} ago`
                  : "Assignment timestamp unavailable"}
              </div>
            </div>
            <div className="rounded-md border bg-white p-3 text-sm">
              <div className="text-xs font-medium uppercase text-muted-foreground">
                Evidence reviewed
              </div>
              <div className="mt-1 font-medium">
                {openedDocumentCount} of {totalDocumentCount} documents reviewed
              </div>
              <div className="text-xs text-muted-foreground">
                {totalDocumentCount === 0
                  ? "No supporting documents are visible to DDO."
                  : "Document open events are recorded by backend audit."}
              </div>
            </div>
            <div className="rounded-md border bg-white p-3 text-sm">
              <div className="text-xs font-medium uppercase text-muted-foreground">
                Applicant claims
              </div>
              <div className="mt-1 font-medium">
                {diligence?.applicant_claims_visible ? "Visible after diligence" : "Hidden"}
              </div>
              <div className="text-xs text-muted-foreground">
                {diligence?.applicant_claims_visible
                  ? "Comparison can now be reviewed."
                  : "Claims stay hidden during blind verification."}
              </div>
            </div>
            <div className="rounded-md border bg-white p-3 text-sm">
              <div className="text-xs font-medium uppercase text-muted-foreground">
                Discrepancies
              </div>
              <div className="mt-1 font-medium">
                {diligence?.unresolved_discrepancy_fields.length ?? 0} unresolved
              </div>
              <div className="text-xs text-muted-foreground">
                {diligence?.unresolved_discrepancy_fields.length
                  ? diligence.unresolved_discrepancy_fields.join(", ")
                  : "No unresolved discrepancy returned by backend."}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Attention */}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Why Attention Is Needed Now
          </CardTitle>
        </CardHeader>

        <CardContent>
          {attentionSignals.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              No immediate attention signal from the returned workflow facts.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
              {visibleAttentionSignals.map((signal) => {
                const Icon = getSignalIcon(signal.type);
                return (
                  <div
                    key={signal.key}
                    className={`rounded-md border p-3 ${signalClasses[signal.type]}`}
                  >
                    <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      <Icon className="h-4 w-4 shrink-0" />
                      <span>{signal.title}</span>
                      <Badge variant="outline">{signalLabels[signal.type]}</Badge>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {signal.detail}
                    </div>
                  </div>
                );
              })}
              </div>
              {hiddenAttentionCount > 0 ? (
                <div className="text-xs text-muted-foreground">
                  {hiddenAttentionCount} additional lower-priority signal(s) hidden to keep the queue calm.
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Workflow Timeline */}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Governance Timeline
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-5">
          {!timeline || timeline.workflow_events.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No workflow transitions recorded yet.
            </p>
          ) : (
            <div className="space-y-4">
              {timeline.workflow_events.map((event) => {
                const tone = getEventTone(event.action_code, event.to_status);
                const Icon = getEventIcon(tone);
                return (
                  <div key={event.id} className="relative pl-7">
                    <span
                      className={`absolute left-0 top-1.5 h-3 w-3 rounded-full ${dotClasses[tone]}`}
                    />
                    <div className={`rounded-md border p-4 text-sm ${toneClasses[tone]}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 font-medium">
                            <Icon className="h-4 w-4" />
                            {formatWorkflowActionLabel(event.action_code)}
                          </div>
                          <div className="text-muted-foreground">
                            {formatStatusLabel(event.from_status)} to{" "}
                            {formatStatusLabel(event.to_status)}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatTimestamp(event.timestamp)}
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Badge variant="outline">Actor {event.actor_id}</Badge>
                        <Badge variant="secondary">{event.actor_role}</Badge>
                        {event.reason_code ? (
                          <Badge variant="outline">{event.reason_code}</Badge>
                        ) : null}
                      </div>
                      {event.reason_text ? (
                        <div className="mt-3 rounded border bg-white/70 p-3">
                          {event.reason_text}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {timeline && timeline.assignment_events.length > 0 ? (
            <div className="border-t pt-4">
              <div className="mb-2 text-sm font-medium">Assignment Route</div>
              <div className="space-y-3">
                {timeline.assignment_events.map((event) => (
                  <div
                    key={event.id}
                    className="rounded-md border bg-slate-50 p-3 text-sm"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="font-medium">
                          Assigned to DDO {event.assigned_to_user_id}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {event.assignment_source === "auto"
                            ? "System routed"
                            : "Manual assignment"}
                          {event.assigned_by_user_id
                            ? ` by user ${event.assigned_by_user_id}`
                            : ""}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatTimestamp(event.assigned_at)}
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Badge variant="outline">{event.role_context}</Badge>
                      <Badge variant="secondary">{event.assignment_source}</Badge>
                      {event.routing_rule_version ? (
                        <Badge variant="outline">
                          {event.routing_rule_version}
                        </Badge>
                      ) : null}
                      {event.unassigned_at ? (
                        <Badge variant="outline">
                          Closed {formatTimestamp(event.unassigned_at)}
                        </Badge>
                      ) : null}
                    </div>
                    {event.routing_reason || event.reason ? (
                      <div className="mt-3 rounded border bg-white p-3 text-xs text-muted-foreground">
                        {event.routing_reason || event.reason}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {timeline && timeline.audit_markers.length > 0 ? (
            <div className="border-t pt-4">
              <div className="mb-2 text-sm font-medium">Audit Markers</div>
              <div className="space-y-2">
                {timeline.audit_markers.map((marker) => (
                  <div
                    key={marker.id}
                    className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground"
                  >
                    <span>
                      {marker.action}
                      {marker.from_state || marker.to_state
                        ? `: ${marker.from_state ?? "-"} -> ${marker.to_state ?? "-"}`
                        : ""}
                    </span>
                    <span>{formatTimestamp(marker.timestamp)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* DDO Diligence */}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Blind Diligence Verification
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
            OCR is assistive only and may be inaccurate. Review documents manually,
            enter verified values independently, then compare against applicant claims.
          </div>

          {!diligence ? (
            <p className="text-sm text-muted-foreground">Diligence projection unavailable.</p>
          ) : (
            <>
              <div className="space-y-2">
                <div className="text-sm font-medium">Uploaded Documents</div>
                {diligence.documents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No uploaded documents are available for diligence.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {diligence.documents.map((document) => {
                      const opened = diligence.documents_opened.some(
                        (event) => event.document_id === document.document_id
                      );
                      return (
                        <div
                          key={document.document_id}
                          className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3 text-sm"
                        >
                          <div>
                            <div className="font-medium">
                              {document.document_type || "Document"}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {document.original_filename || document.document_id}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {document.scan_status ? (
                              <Badge variant="secondary">
                                Scan {document.scan_status}
                              </Badge>
                            ) : null}
                            {opened ? <Badge variant="secondary">Opened</Badge> : null}
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={diligenceBusy}
                              onClick={() => handleDocumentOpen(document.document_id)}
                            >
                              Mark reviewed
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {!diligence.diligence_submitted ? (
                <div className="space-y-3 border-t pt-4">
                  <div className="text-sm font-medium">
                    Enter DDO-verified values before applicant claims are revealed
                  </div>
                  {diligenceRows.map((row, index) => (
                    <div key={index} className="grid gap-2 md:grid-cols-2">
                      <input
                        className="rounded-md border px-3 py-2 text-sm"
                        placeholder="Field name, e.g. aadhaar_number"
                        value={row.field_name}
                        onChange={(event) => {
                          const next = [...diligenceRows];
                          next[index] = { ...row, field_name: event.target.value };
                          setDiligenceRows(next);
                        }}
                      />
                      <input
                        className="rounded-md border px-3 py-2 text-sm"
                        placeholder="Value verified from document"
                        value={row.value}
                        onChange={(event) => {
                          const next = [...diligenceRows];
                          next[index] = { ...row, value: event.target.value };
                          setDiligenceRows(next);
                        }}
                      />
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      onClick={() =>
                        setDiligenceRows([...diligenceRows, { field_name: "", value: "" }])
                      }
                    >
                      Add field
                    </Button>
                    <Button disabled={diligenceBusy} onClick={submitDiligence}>
                      Submit blind diligence
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-sm font-medium">Post-Diligence Comparison</div>
                  {diligence.comparisons.map((comparison) => {
                    const draft =
                      resolutionDrafts[comparison.field_name] || {
                        finalValue: String(comparison.ddo_verified_value ?? ""),
                        reason: "",
                      };
                    const resolved = Boolean(diligence.resolutions[comparison.field_name]);
                    return (
                      <div
                        key={comparison.field_name}
                        className="rounded-md border p-3 text-sm"
                      >
                        <div className="flex flex-wrap items-center gap-2 font-medium">
                          {comparison.field_name}
                          {comparison.discrepancy_detected ? (
                            <Badge variant={resolved ? "secondary" : "outline"}>
                              {resolved ? "Resolved" : "Discrepancy"}
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Matched</Badge>
                          )}
                        </div>
                        <div className="mt-2 grid gap-2 md:grid-cols-3">
                          <div>
                            <div className="text-xs text-muted-foreground">
                              Applicant claim
                            </div>
                            <div>{String(comparison.applicant_claim_value ?? "-")}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">
                              DDO verified
                            </div>
                            <div>{String(comparison.ddo_verified_value ?? "-")}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">
                              OCR candidate
                            </div>
                            <div>{String(comparison.ocr_extracted_value ?? "-")}</div>
                          </div>
                        </div>
                        {comparison.discrepancy_detected && !resolved ? (
                          <div className="mt-3 grid gap-2 md:grid-cols-[1fr_2fr_auto]">
                            <input
                              className="rounded-md border px-3 py-2"
                              placeholder="Final value"
                              value={draft.finalValue}
                              onChange={(event) =>
                                setResolutionDrafts({
                                  ...resolutionDrafts,
                                  [comparison.field_name]: {
                                    ...draft,
                                    finalValue: event.target.value,
                                  },
                                })
                              }
                            />
                            <input
                              className="rounded-md border px-3 py-2"
                              placeholder="Resolution reason"
                              value={draft.reason}
                              onChange={(event) =>
                                setResolutionDrafts({
                                  ...resolutionDrafts,
                                  [comparison.field_name]: {
                                    ...draft,
                                    reason: event.target.value,
                                  },
                                })
                              }
                            />
                            <Button
                              variant="outline"
                              disabled={diligenceBusy}
                              onClick={() => resolveDiligenceField(comparison.field_name)}
                            >
                              Resolve
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Workflow Actions */}

      <Card>
        <CardHeader>
          <CardTitle>Available Actions</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <Textarea
            placeholder="Optional remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />

          {actions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No workflow actions available.
            </p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {actions.map((action) => (
                <Button
                  key={action.action_code}
                  variant={getActionVariant(action.action_code)}
                  disabled={!!transitioningAction}
                  onClick={() => handleTransition(action.action_code)}
                >
                  {transitioningAction === action.action_code
                    ? "Processing..."
                    : `${formatWorkflowActionLabel(action.action_code)} → ${formatWorkflowStatusLabel(action.to_status)}`}
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Back */}

      <Button
        variant="outline"
        onClick={() => navigate(`/${tenantSlug}/ddo/submissions`)}
      >
        Back to Queue
      </Button>
    </div>
  );
};

export default DDOSubmissionReviewPage;
